/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Anchor, Card, Divider, Group, Paper, Stack, Text, Tooltip, Collapse, ActionIcon } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
// import { IconExternalLink } from "@tabler/icons-react";
import HubspotIcon from "../../../../assets/images/hubspot.svg?react";
import SalesforceIcon from "../../../../assets/images/salesforce.svg?react";
import { formatDistance } from "date-fns";
import TruncatedTextTooltip from "../../../../shared/ui/truncate/TruncatedText";

type ProviderDescriptor = {
  key: string;
  label: string;
  urlField: string;
  idField: string;
  nameField: string;
  firstNameField: string;
  lastNameField: string;
  defaultNameField: string;
  lastInField: string;
  lastOutField: string;
  color?: string;
  icon?: 'hubspot' | 'salesforce';
};

const PROVIDERS: ProviderDescriptor[] = [
  {
    key: 'salesforce',
    label: 'Salesforce',
    urlField: 'salesforce_url',
    idField: 'salesforce_record_id',
    nameField: 'salesforce_name',
    firstNameField: 'salesforce_first_name',
    lastNameField: 'salesforce_last_name',
    defaultNameField: 'name',
    lastInField: 'salesforce_external_last_sync_write_in',
    lastOutField: 'salesforce_external_last_sync_write_out',
    color: 'blue',
    icon: 'salesforce',
  },
  {
    key: 'hubspot',
    label: 'HubSpot',
    urlField: 'hubspot_url',
    idField: 'hubspot_record_id',
    nameField: 'hubspot_name',
    firstNameField: 'hubspot_first_name',
    lastNameField: 'hubspot_last_name',
    defaultNameField: 'name',
    lastInField: 'hubspot_external_last_sync_write_in',
    lastOutField: 'hubspot_external_last_sync_write_out',
    color: 'orange',
    icon: 'hubspot',
  },
];

interface IntegratedSourcesProps {
  account: Record<string, any> | null | undefined;
}

const formatWhen = (value: any) => {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return formatDistance(d, new Date(), { addSuffix: true });
  } catch {
    return null;
  }
};

export default function IntegratedSources({ account }: IntegratedSourcesProps) {
  const [opened, { toggle }] = useDisclosure(true);
  if (!account) return null;
  const sources = PROVIDERS.map((p) => {
    const url = account[p.urlField];
    const name: any = account[p.nameField] || account[p.firstNameField] || account[p.defaultNameField];
    const id = account[p.idField];
    const lastIn = formatWhen(account[p.lastInField]);
    const lastOut = formatWhen(account[p.lastOutField]);
    return { ...p, url, id, lastIn, lastOut, name };
  }).filter((s) => s.url || s.id || s.lastIn || s.lastOut);

  if (sources.length === 0) return null;

  return (
    <Paper>
      <Stack gap={8}>
        <Group justify="space-between" align="center">
          <Divider label="External Links" labelPosition="left" style={{ flex: 1 }} />
          <ActionIcon variant="subtle" onClick={toggle} aria-label={opened ? 'Collapse' : 'Expand'}>
            <IconChevronDown size={14} style={{ transform: opened ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />
          </ActionIcon>
        </Group>
        <Collapse in={opened}>
          <Stack gap={10}>
            {sources.map((s, idx) => {
              const syncParts: string[] = [];
              if (s.lastIn) syncParts.push(`Imported ${s.lastIn}`);
              const syncLabel = syncParts.join(' • ');

              const card = (
                <Card withBorder radius="md" p="xs" className="shadow-sm bg-orange-50/80">
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap={10} align="center" wrap="nowrap">
                      <Stack gap={0}>
                        <Tooltip label={`${s.label} Record ID: ${s.id}`}>
                          <Text lh={2} size="xs" fw={600}>{s.name}</Text>
                        </Tooltip>
                      </Stack>
                    </Group>
                    <Stack gap={0} align="end">
                      <Group gap={8} align="center" wrap="nowrap">
                        {syncLabel && (
                          <TruncatedTextTooltip width={100} size="9" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{syncLabel}</TruncatedTextTooltip>
                        )}
                        <span style={{ display: 'inline-flex', width: 20, height: 20, alignItems: 'center' }}>
                          {s.icon === 'hubspot' ? (
                            <HubspotIcon width={16} height={16} />
                          ) : s.icon === 'salesforce' ? (
                            <SalesforceIcon width={16} height={16} />
                          ) : null}
                        </span>
                      </Group>
                    </Stack>

                  </Group>
                </Card>
              );

              return (
                <div key={`${s.key}-${idx}`}>
                  {s.url ? (
                    <Anchor href={s.url as string} target="_blank" rel="noopener noreferrer" underline="never" style={{ display: 'block' }}>
                      {card}
                    </Anchor>
                  ) : (
                    card
                  )}
                </div>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
